// Front-end navigation catalogue.
//
// Two kinds of areas (ADR 010):
//  - PROJECT_FEATURES — project-scoped (Quality & Inspection). Rendered in the
//    sidebar when you're inside a project; links are nested under
//    /projects/:projectId/… via route *names*.
//  - WORKFORCE_FEATURES — cross-project (workers span projects). Top-level,
//    flat paths.

export interface ProjectFeature {
  /** Vue Router route name; resolved to /projects/:projectId/… at render. */
  name: string;
  label: string;
  /** SVG path `d` for a 24x24 stroked icon. */
  icon: string;
  blurb: string;
}

export interface AreaFeature {
  to: string;
  label: string;
  icon: string;
  blurb: string;
}

// Inside a project. The Dashboard is the project's landing view.
export const PROJECT_FEATURES: ProjectFeature[] = [
  {
    name: "project-dashboard",
    label: "Dashboard",
    icon: "M3 12l9-9 9 9M5 10v10h14V10",
    blurb: "This project at a glance.",
  },
  {
    name: "project-progress",
    label: "Progress",
    icon: "M3 3v18h18M18 17V9M13 17V5M8 17v-3",
    blurb: "Live location completion against the task list, from approved QA.",
  },
  {
    name: "project-team",
    label: "Team",
    icon: "M17 20h5v-2a4 4 0 00-3-3.87M9 20H4v-2a4 4 0 013-3.87M12 12a4 4 0 100-8 4 4 0 000 8z",
    blurb: "Assign workers to this project — controls who can access it.",
  },
  {
    name: "work-packs",
    label: "Work packs",
    icon: "M21 16V8a2 2 0 00-1-1.73l-7-4a2 2 0 00-2 0l-7 4A2 2 0 003 8v8a2 2 0 001 1.73l7 4a2 2 0 002 0l7-4A2 2 0 0021 16zM3.27 6.96L12 12l8.73-5.04M12 22.08V12",
    blurb: "Office-issued packs of shift documents.",
  },
  {
    name: "site-diaries",
    label: "Site diaries",
    icon: "M8 4h9a2 2 0 012 2v14H8M8 4a2 2 0 00-2 2v12a2 2 0 002 2M8 4v16M11 8h5M11 12h5",
    blurb: "Submitted and in-progress Site Diary Reports.",
  },
  {
    name: "drawings",
    label: "Drawings",
    icon: "M4 3h10l6 6v12H4zM14 3v6h6M8 13h8M8 17h5",
    blurb: "Project plans and drawings — view, page through, and mark up.",
  },
  {
    name: "briefings",
    label: "Briefings",
    icon: "M17 20h5v-2a4 4 0 00-3-3.87M9 20H4v-2a4 4 0 013-3.87M12 12a4 4 0 100-8 4 4 0 000 8z",
    blurb: "Task briefings and the digital sign-on registers.",
  },
  {
    name: "check-sheets",
    label: "Check sheets",
    icon: "M9 11l3 3L22 4M21 12v7a2 2 0 01-2 2H5a2 2 0 01-2-2V5a2 2 0 012-2h11",
    blurb: "QA / ITP check sheets completed on site, awaiting CRE approval.",
  },
  {
    name: "quality-insights",
    label: "Quality insights",
    icon: "M3 3v18h18M7 14l3-3 3 3 5-6",
    blurb: "Trends, recurring non-conformances and defect Pareto across ITPs.",
  },
  {
    name: "project-issues",
    label: "Issues",
    icon: "M12 9v4m0 4h.01M10.3 3.86l-8.5 14.7A2 2 0 003.5 22h17a2 2 0 001.73-3.44l-8.5-14.7a2 2 0 00-3.46 0z",
    blurb: "Snags, defects, tasks, RFIs, observations & inspections in one board.",
  },
  {
    name: "handover",
    label: "Handover",
    icon: "M9 12l2 2 4-4M7.835 4.697a3.42 3.42 0 001.946-.806 3.42 3.42 0 014.438 0 3.42 3.42 0 001.946.806 3.42 3.42 0 013.138 3.138 3.42 3.42 0 00.806 1.946 3.42 3.42 0 010 4.438 3.42 3.42 0 00-.806 1.946 3.42 3.42 0 01-3.138 3.138 3.42 3.42 0 00-1.946.806 3.42 3.42 0 01-4.438 0 3.42 3.42 0 00-1.946-.806 3.42 3.42 0 01-3.138-3.138 3.42 3.42 0 00-.806-1.946 3.42 3.42 0 010-4.438 3.42 3.42 0 00.806-1.946 3.42 3.42 0 013.138-3.138z",
    blurb: "Close-out: clear the snag list, then sign off practical completion.",
  },
  {
    name: "observations",
    label: "Close calls & good practice",
    icon: "M12 4.5C7 4.5 2.7 7.6 1 12c1.7 4.4 6 7.5 11 7.5s9.3-3.1 11-7.5c-1.7-4.4-6-7.5-11-7.5zm0 12a4.5 4.5 0 110-9 4.5 4.5 0 010 9z",
    blurb: "Safety observations raised from the diary.",
  },
  {
    name: "safety-insights",
    label: "Safety insights",
    icon: "M3 3v18h18M7 14l3-3 3 3 5-6M9 12l2 2 4-4",
    blurb: "Close-call trends and the 'We Said, We Did' board for toolbox talks.",
  },
  {
    name: "weekly-reports",
    label: "Weekly reports",
    icon: "M5 3a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2V5a2 2 0 00-2-2H5zm4 14v-6m3 6v-4m3 4v-8",
    blurb: "The weekly project report — generated from the week's diaries.",
  },
  {
    name: "structures",
    label: "Structures",
    icon: "M12 2v20M5 8l7-5 7 5M5 8v8l7 5 7-5V8",
    blurb: "The project's locations (poles, cantilevers) — what check-sheets attach to.",
  },
  {
    name: "documents",
    label: "Documents",
    icon: "M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8l-6-6zM14 2v6h6M9 13h6M9 17h6",
    blurb: "Reference document library — project and company-wide.",
  },
];

// Project "meta" surfaces — about the project, not the work. Rendered as a
// divided utility group pinned to the bottom of the sidebar. (Settings joins
// this group once built.)
export const PROJECT_UTILITIES: ProjectFeature[] = [
  {
    name: "activity",
    label: "Activity",
    icon: "M22 12h-4l-3 9L9 3l-3 9H2",
    blurb: "Live timeline of everything happening on the project.",
  },
  {
    name: "notifications",
    label: "Notifications",
    icon: "M18 8a6 6 0 00-12 0c0 7-3 9-3 9h18s-3-2-3-9M13.73 21a2 2 0 01-3.46 0",
    blurb: "What needs attention — items waiting on someone to act.",
  },
  {
    name: "settings",
    label: "Settings",
    icon: "M12 15a3 3 0 100-6 3 3 0 000 6zM19.4 15a1.65 1.65 0 00.33 1.82l.06.06a2 2 0 11-2.83 2.83l-.06-.06a1.65 1.65 0 00-1.82-.33 1.65 1.65 0 00-1 1.51V21a2 2 0 01-4 0v-.09A1.65 1.65 0 008 19.4a1.65 1.65 0 00-1.82.33l-.06.06a2 2 0 11-2.83-2.83l.06-.06a1.65 1.65 0 00.33-1.82 1.65 1.65 0 00-1.51-1H3a2 2 0 010-4h.09A1.65 1.65 0 004.6 8a1.65 1.65 0 00-.33-1.82l-.06-.06a2 2 0 112.83-2.83l.06.06a1.65 1.65 0 001.82.33H9a1.65 1.65 0 001-1.51V3a2 2 0 014 0v.09a1.65 1.65 0 001 1.51 1.65 1.65 0 001.82-.33l.06-.06a2 2 0 112.83 2.83l-.06.06a1.65 1.65 0 00-.33 1.82V9a1.65 1.65 0 001.51 1H21a2 2 0 010 4h-.09a1.65 1.65 0 00-1.51 1z",
    blurb: "Project details and configuration.",
  },
];

// Cross-project areas — reached from the header, not tied to a project.
export const WORKFORCE_FEATURES: AreaFeature[] = [
  {
    to: "/workers",
    label: "Workers",
    icon: "M17 20h5v-2a4 4 0 00-3-3.87M9 20H4v-2a4 4 0 013-3.87M12 12a4 4 0 100-8 4 4 0 000 8zm6-4a3 3 0 11-6 0 3 3 0 016 0z",
    blurb: "The worker master — names, NCCA, contact and compliance details.",
  },
  {
    to: "/timesheets",
    label: "Timesheets",
    icon: "M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01",
    blurb: "Weekly timesheets — planned vs actual, adjust, approve, export.",
  },
  {
    to: "/compliance",
    label: "Compliance",
    icon: "M9 12l2 2 4-4m5.6 1.6A9 9 0 1112 3a9 9 0 019.6 9.6z",
    blurb: "What needs chasing — cards, medicals and OH across the workforce.",
  },
  {
    to: "/workforce-map",
    label: "Workforce map",
    icon: "M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4m0 0L9 7",
    blurb: "Where each worker last signed on, and who's using the app.",
  },
  {
    to: "/fatigue",
    label: "Fatigue & risk",
    icon: "M13 2L3 14h9l-1 8 10-12h-9l1-8z",
    blurb: "HSE Fatigue & Risk Index (RR446) and working-time alerts from the roster.",
  },
  {
    to: "/accommodations",
    label: "Travel & lodging",
    icon: "M3 21h18M4 21V9l8-5 8 5v12M9 21v-6h6v6",
    blurb: "Transport modes + reusable hotel / lodging catalogue for travel time.",
  },
];

/** Routes that belong to the cross-project Workforce area (not a project). */
export function isWorkforceRoute(path: string): boolean {
  return WORKFORCE_FEATURES.some(
    (f) => path === f.to || path.startsWith(`${f.to}/`),
  );
}
